import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Award, 
  TrendingUp, 
  Shield, 
  Heart,
} from "lucide-react";
import logo from "@/assets/logo.webp";
import SocialShare from "@/components/social/SocialShare";
import { SEOHead } from "@/components/seo";
import { generateOrganizationSchema, generateAboutPageSchema } from "@/components/seo/SchemaMarkup";

const About = () => {
  useEffect(() => {
    document.title = "About Us | Prime Realty Partners";
  }, []);

  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "We believe in transparent, honest communication throughout every transaction.",
    },
    {
      icon: Heart,
      title: "Client-First",
      description: "Your goals are our priority. We listen, understand, and deliver results.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We strive for excellence in every interaction and transaction.",
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "Leveraging the latest technology to give you a competitive edge.",
    },
  ];

  const organizationSchema = generateOrganizationSchema();
  const aboutPageSchema = generateAboutPageSchema();

  return (
    <Layout>
      <SEOHead
        title="About Us | Prime Realty Partners"
        description="Prime Realty Partners is a full-service brokerage serving 49 South Jersey communities. Experienced agents, local expertise, proven results."
        canonicalUrl="https://www.prpsj.com/about"
        keywords="about Prime Realty Partners, New Jersey real estate company, NJ home buying experts, trusted realtor NJ"
        schema={[organizationSchema, aboutPageSchema]}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "About", url: "https://www.prpsj.com/about" }]}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <img 
              src={logo} 
              alt="Prime Realty Partners logo" 
              width={160}
              height={96}
              className="h-24 w-auto mx-auto mb-6"
            />
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              About Prime Realty Partners
            </h1>
            <p className="text-lg text-muted-foreground">
              We've helped countless families buy and sell homes across South Jersey. 
              Our hands-on approach and dedication to every client have earned us a 
              reputation as one of the region's most trusted real estate teams.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-6 text-center">
              Our Story
            </h2>
            <div className="prose prose-lg mx-auto text-muted-foreground">
              <p className="mb-4">
                Founded in 2019 in the heart of Haddonfield, Prime Realty Partners began 
                as a small, service driven brokerage with a big vision. From day one, our 
                mission has been simple: deliver exceptional real estate experiences built 
                on integrity, professionalism, and a genuine commitment to our clients.
              </p>
              <p className="mb-4">
                Year after year, our company has continued to grow thanks to our strong 
                belief in personalized service and a can-do attitude toward every transaction. 
                We pride ourselves on being trusted advisors, dedicated advocates, and 
                knowledgeable market experts who guide our clients with confidence and care.
              </p>
              <p className="mb-4">
                At Prime Realty Partners, we believe real estate is more than buying and 
                selling properties. It is about building relationships, strengthening 
                communities, and helping our clients achieve their goals. Our continued 
                growth is a reflection of the trust our clients place in us and the passion 
                our team brings to every opportunity.
              </p>
              <p>
                Whether you are buying, selling, or investing, we are here to provide the 
                experience, support, and results you deserve.
              </p>
            </div>
            
            {/* Social Share */}
            <div className="mt-8 flex justify-center">
              <SocialShare 
                title="About Prime Realty Partners" 
                description="Learn about our commitment to excellence in real estate."
                showLabel 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <h2 className="font-heading text-3xl font-bold mb-10 text-center">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Whether you're buying, selling, or just exploring your options, 
            our team is here to help you navigate your real estate journey.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://homes.prpsj.com/idx/results/listings" rel="noopener noreferrer">
              <Button size="lg" variant="secondary">
                Browse Properties
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-primary border-2 border-white hover:bg-white/90">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
